import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import {
  changeManageUpdatePreferencesV2,
  fetchSettingsManageUpdates
} from '@actions/auth';
import Image from '@base/Image';
import useCountries from '@api/hooks/useCountries';
import useParentContact from '@api/hooks/parentContact/useParentContact';
import {
  PARENT_CONTACT_ANALYTICS_SOURCES,
  trackAddParentContactClicked
} from '@stores/parent-contact/parentContactAnalytics';
import useSubscribed from '@hooks/useSubscribed';
import { getStore } from 'src/redux';
import ParentContactVerificationDrawer from './ParentContactVerificationDrawer';
import useShouldShowParentProgressFloater from './useShouldShowParentProgressFloater';
import { isParentProgressManageUpdateBlock } from './parentProgressFloaterUtils';

interface ParentProgressFloaterProps {
  goalUID?: string;
}

const FabButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:hover {
    opacity: 0.95;
  }
`;

const ParentProgressFloater = ({ goalUID }: ParentProgressFloaterProps) => {
  const shouldShowFloater = useShouldShowParentProgressFloater(goalUID);
  const countries = useCountries();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const userHasSubscription = useSubscribed(goalUID);

  const { refreshStatus, sendOtp, verifyOtp } = useParentContact();

  useEffect(() => {
    if (shouldShowFloater) getStore().dispatch(fetchSettingsManageUpdates());
  }, [shouldShowFloater]);

  if (!shouldShowFloater) {
    return null;
  }

  const handleFabClick = () => {
    trackAddParentContactClicked({
      source: PARENT_CONTACT_ANALYTICS_SOURCES.HOME_BANNER,
      userHasSubscription
    });
    setIsDrawerOpen(true);
  };

  const handleVerified = async () => {
    await refreshStatus();
    const store = getStore();
    const { manageUpdates } = store.getState()?.auth ?? {};
    const parentProgressBlockType =
      manageUpdates && !Array.isArray(manageUpdates)
        ? Object.keys(manageUpdates).find((blockType) =>
            isParentProgressManageUpdateBlock(
              blockType,
              manageUpdates[blockType]
            )
          )
        : undefined;

    if (parentProgressBlockType) {
      store.dispatch(
        changeManageUpdatePreferencesV2(
          parentProgressBlockType,
          'whatsapp',
          true
        )
      );
    }
    setIsDrawerOpen(false);
  };

  return (
    <>
      <FabButton
        type="button"
        onClick={handleFabClick}
        aria-label="Enable WhatsApp progress updates for parents"
      >
        <Image
          src="whatsappUser.svg"
          width={86}
          height={48}
          alt="WhatsApp parent updates"
        />
      </FabButton>
      <ParentContactVerificationDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onVerified={handleVerified}
        sendOtp={sendOtp}
        verifyOtp={verifyOtp}
        countries={countries}
        analyticsSource={PARENT_CONTACT_ANALYTICS_SOURCES.HOME_BANNER}
        userHasSubscription={userHasSubscription}
      />
    </>
  );
};

export default ParentProgressFloater;
